import { HorizontalNavigationTab } from "@risalabs_frontend_org/oasis-ui-kit";
import { useEffect, useMemo, useState, type ReactNode } from "react";
import { BusniessOfficeNote } from "../../../../data-model/busniessOfficeNote";
import type { SingleCoverageInformation } from "../../../../data-model/coverageInformation";
import type { GeneralOrderDetails } from "../../../../data-model/medicalPaOrdersModel";
import { CoveragePlanDisplayStatus } from "../../../../enums/coverageInformationEnums";
import { InfoIcon } from "../../../../svg/info-icon";
import type { PlanActivityStatus } from "../../helpers/coverageStatusUtils";
import type { ManualEvFieldEditedDetail } from "../../helpers/evCoverageAnalyticsEvents";
import type { EvCoverageEditValidationResult } from "../../helpers/evCoverageEditValidation";
import EvCoverageFinancialContent from "../evCoverageActiveContent/evCoverageActiveContent";
import EvCoverageEmptyState from "../evCoverageEmptyState/evCoverageEmptyState";
import InactiveInsurance from "../inactiveInsurance/inactiveInsurance";
import EvCoverageAdditionalPayersContent from "./evCoverageAdditionalPayersContent";
import { EvCoverageMedicaidContent } from "./evCoverageMedicaidContent";
import type { UseEvCoverageLocalEditStateResult } from "./useEvCoverageLocalEditState";

export interface EvCoverageInactiveInsuranceProps {
  onCommentsChange: (comment: string) => void;
  queryType: string;
  status: string;
  id: string;
  reports: BusniessOfficeNote[];
  generalOrderDetails?: GeneralOrderDetails;
}

/**
 * Tab value for the "Additional Payers" inner tab. A non-numeric sentinel so
 * it never flows into the numeric `treatmentTab` state (which indexes
 * `patientResponsibilityInformation`).
 */
const ADDITIONAL_PAYER_TAB = "additionalPayers";

export interface EvCoverageActivePlanAndFinancialsProps {
  planSummary: ReactNode;
  displayCoverage: SingleCoverageInformation;
  isEditing: boolean;
  isActive: PlanActivityStatus;
  serviceTypeCodeLabelMap?: ReadonlyMap<string, string>;
  treatmentTab: string;
  onTreatmentTabChange: (value: string) => void;
  onApplyCoverageEdit: UseEvCoverageLocalEditStateResult["applyCoverageEdit"];
  onManualEvFieldEdited?: (detail: ManualEvFieldEditedDetail) => void;
  showFieldErrors: boolean;
  invalidFieldIds: EvCoverageEditValidationResult["invalidFieldIds"];
  inactiveInsuranceProps: EvCoverageInactiveInsuranceProps;
}

/**
 * Info panel shown when BV values do not apply because the plan status is inactive.
 *
 * @returns The "not applicable" message panel.
 */
const InactiveBvNotApplicablePanel = () => (
  <div className="flex min-h-0 flex-1 flex-col items-center justify-center overflow-auto rounded-lg border border-primaryGray-15 bg-white p-4">
    <div className="flex w-full max-w-4xl flex-col items-center justify-center gap-3 px-4 py-6">
      <InfoIcon height="28" width="28" stroke="#0056D6" strokeWidth="1.5" />
      <p className="text-center text-small font-normal text-tertiaryBlue-2">
        Benefit verification values are not applicable when plan status is
        inactive.
      </p>
    </div>
  </div>
);

/**
 * Active EV path: editable plan summary, treatment sub-tabs, and financial content.
 * Falls back to an empty state (no PR rows), the inactive-insurance view, or a
 * "not applicable" panel when BV editing does not apply to the current plan status.
 *
 * @param props - {@link EvCoverageActivePlanAndFinancialsProps}
 * @returns The plan-and-financials column.
 */
export function EvCoverageActivePlanAndFinancials({
  planSummary,
  displayCoverage,
  isEditing,
  isActive,
  serviceTypeCodeLabelMap,
  treatmentTab,
  onTreatmentTabChange,
  onApplyCoverageEdit,
  onManualEvFieldEdited,
  showFieldErrors,
  invalidFieldIds,
  inactiveInsuranceProps,
}: EvCoverageActivePlanAndFinancialsProps) {
  const prEntries = displayCoverage.patientResponsibilityInformation ?? [];

  const treatmentTabs = useMemo(() => {
    return prEntries.map((entry, index) => {
      const serviceTypeInformation =
        entry.singlePatientResponsibility.serviceTypeInformation;
      const desc = serviceTypeInformation.serviceTypeDescription || "Treatment";
      const serviceTypeNumber = serviceTypeInformation.serviceType;
      const codeKey = String(serviceTypeNumber ?? "").trim();
      const serviceTypeValueLabel =
        codeKey && serviceTypeCodeLabelMap?.get(codeKey);
      const tabLabel = serviceTypeValueLabel || desc;

      return {
        value: String(index),
        label: tabLabel,
        rightContent: serviceTypeNumber ? (
          <span className="flex items-center justify-center gap-2.5 rounded bg-primaryGray-16 px-1.5 py-0.5 text-x-tiny font-bold leading-x-tiny text-primaryGray-1">
            {serviceTypeNumber}
          </span>
        ) : undefined,
      };
    });
  }, [prEntries, serviceTypeCodeLabelMap]);

  const isBvEditApplicable =
    !isEditing ||
    displayCoverage.planInformation.planStatus !==
      CoveragePlanDisplayStatus.INACTIVE;

  const hasPatientResponsibilityData = prEntries.length > 0;
  const medicaidInformation = displayCoverage.medicaidInformation;
  const additionalPayerInformation = displayCoverage.additionalPayerInformation;
  const showAdditionalPayerTab =
    (additionalPayerInformation?.payers.length ?? 0) > 0;
  const [showAdditionalPayers, setShowAdditionalPayers] = useState(false);

  const tabItems = useMemo(() => {
    const additionalPayerTab = showAdditionalPayerTab
      ? [
          {
            value: ADDITIONAL_PAYER_TAB,
            label: "Additional Payers",
          },
        ]
      : [];
    return [...treatmentTabs, ...additionalPayerTab];
  }, [treatmentTabs, showAdditionalPayerTab]);

  const handleTabChange = (value: string) => {
    if (value === ADDITIONAL_PAYER_TAB) {
      setShowAdditionalPayers(true);
      return;
    }
    setShowAdditionalPayers(false);
    onTreatmentTabChange(value);
  };

  // The parent can move the active treatment tab externally (e.g. Continue
  // validation focuses the failing field). That must not leave the "Additional
  // Payers" tab selected — the tab selection follows the numeric treatment
  // tab, so reset the local flag whenever the treatment tab changes.
  useEffect(() => {
    setShowAdditionalPayers(false);
  }, [treatmentTab]);

  if (medicaidInformation) {
    return (
      <div className="flex min-h-0 flex-1 flex-col gap-2 overflow-hidden pt-2 md:gap-3 md:pt-4">
        <div className="shrink-0">{planSummary}</div>
        {medicaidInformation.hasNycscDetails ? (
          <EvCoverageMedicaidContent
            medicaidInformation={medicaidInformation}
          />
        ) : (
          <EvCoverageEmptyState variant="medicaidDetailsUnavailable" />
        )}
      </div>
    );
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-2 overflow-hidden pt-2 md:gap-3 md:pt-4">
      <div className="shrink-0">{planSummary}</div>
      {isBvEditApplicable ? (
        hasPatientResponsibilityData || isEditing || showAdditionalPayerTab ? (
          <>
            {tabItems.length > 0 && (
              <div className="w-full min-w-0 shrink-0 overflow-x-auto">
                <HorizontalNavigationTab
                  tabs={tabItems}
                  value={
                    showAdditionalPayers ? ADDITIONAL_PAYER_TAB : treatmentTab
                  }
                  onChange={handleTabChange}
                  rounded
                  className="w-max min-w-full shrink-0 border-b border-primaryGray-15"
                  backgroundColor="#fff"
                />
              </div>
            )}
            <div className="ev-coverage-unified__financial-scroll min-h-0 flex-1 overflow-y-auto">
              {showAdditionalPayers && additionalPayerInformation ? (
                <EvCoverageAdditionalPayersContent
                  payers={additionalPayerInformation.payers}
                />
              ) : (
                <EvCoverageFinancialContent
                  singleCoverage={displayCoverage}
                  activeTreatmentTab={treatmentTab}
                  isEditing={isEditing}
                  onApplyCoverageEdit={onApplyCoverageEdit}
                  onManualEvFieldEdited={onManualEvFieldEdited}
                  showFieldErrors={showFieldErrors}
                  invalidFieldIds={invalidFieldIds}
                />
              )}
            </div>
          </>
        ) : (
          <EvCoverageEmptyState variant="noPatientResponsibility" />
        )
      ) : isActive === "inactive" ? (
        <div className="flex min-h-0 flex-1 flex-col overflow-auto py-2 md:py-4">
          <InactiveInsurance {...inactiveInsuranceProps} />
        </div>
      ) : (
        <InactiveBvNotApplicablePanel />
      )}
    </div>
  );
}

export default EvCoverageActivePlanAndFinancials;
